import type { PriceBreakdown, Pricing } from '@/types/checkout';

const MULTIPLIER_DECIMAL_MAP = new Map([
  [1, 0],
  [100, 2],
  [1000, 3],
] as const);

const DEFAULT_DECIMAL_PLACES = 2;

const getDecimalPlaces = (multiplier?: number): number => {
  if (!multiplier) return DEFAULT_DECIMAL_PLACES;
  return MULTIPLIER_DECIMAL_MAP.get(multiplier as 1 | 100 | 1000) ?? DEFAULT_DECIMAL_PLACES;
};

export const getFormattedNumber = (number: number, pricing: Pricing): string => {
  const decimalNumber = pricing.isCents && pricing.multiplier ? number / pricing.multiplier : number;

  const decimalPlaces = getDecimalPlaces(pricing.multiplier);

  const fixedNumber = decimalPlaces === 0 ? Math.round(decimalNumber).toString() : decimalNumber.toFixed(decimalPlaces);

  const [integerPart, decimalPart] = fixedNumber.split('.');

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, pricing.milSeparator);

  const formattedDecimal = decimalPart || (decimalPlaces > 0 ? '0'.repeat(decimalPlaces) : '');

  return decimalPlaces === 0
    ? formattedInteger
    : `${formattedInteger}${pricing.fractionalSeparator}${formattedDecimal}`;
};

export const getFormattedCurrency = (currency: string): string => {
  return currency.toUpperCase();
};

export const getFormattedCountryCode = (countryCode: string): string => {
  return countryCode.toUpperCase();
};

/** Formats an arbitrary amount with the publisher's currency symbol, honoring `symbolPosition` and `spacing`. */
export const formatPriceWithCurrency = (price: number, pricing: Pricing): string => {
  const formattedNumber = getFormattedNumber(price, pricing);
  const space = pricing.spacing ? ' ' : '';
  return pricing.symbolPosition === 'Left'
    ? `${pricing.currencySymbol}${space}${formattedNumber}`
    : `${formattedNumber}${space}${pricing.currencySymbol}`;
};

/** Total = final price (after discount, with tax). Matches v2: use priceTotal only. */
export const formatTotalPrice = (pricing: Pricing): string => {
  return formatPriceWithCurrency(pricing.priceBreakdown.priceTotal, pricing);
};

export const formatSubtotalPrice = (priceBreakdown: PriceBreakdown, pricing: Pricing): string => {
  return formatPriceWithCurrency(priceBreakdown.baseNet, pricing);
};

export const formatDiscountPrice = (priceBreakdown: PriceBreakdown, pricing: Pricing): string => {
  return formatPriceWithCurrency(priceBreakdown.discount, pricing);
};

export const formatTaxPrice = (priceBreakdown: PriceBreakdown, pricing: Pricing): string => {
  if (!priceBreakdown.isTaxValid) return '–';
  return formatPriceWithCurrency(priceBreakdown.tax, pricing);
};

export const convertToDecimalAmount = (num: number, multiplier: number): string => {
  return multiplier === 1 ? num.toString() : (num / multiplier).toString();
};

export const formatTaxPercent = (priceBreakdown: PriceBreakdown): string => {
  return `${priceBreakdown.taxRatePoints / 100}%`;
};

export const addCommasToNumber = (number: bigint): string => {
  return number.toLocaleString();
};

/*
 * Get payment request amount for Adyen/Google Pay transactionInfo.
 */
export const getPaymentRequestAmount = (number?: number, pricing?: Pricing): string => {
  if (number == null || Number.isNaN(number)) return '0.00';
  const value = pricing?.isCents && pricing?.multiplier ? number / pricing.multiplier : number;
  const decimalPlaces = getDecimalPlaces(pricing?.multiplier);
  return value.toFixed(decimalPlaces);
};

/**
 * Wallet sheet amounts are display-only for Apple Pay (server charges openOrder).
 * Google Pay `createPayment` must use the same major amount string as Nuvei openOrder.
 *
 * Prefer `multiplier` when present — do not rely on `isCents` alone (mis-set
 * `isCents:false` with minor-unit totals would send raw ints like `"2177500"`).
 */
const getMajorAmount = (number: number | undefined, pricing: Pricing | undefined): number => {
  if (number == null || Number.isNaN(number)) return 0;
  return pricing?.multiplier ? number / pricing.multiplier : number;
};

const getRoundedPaymentRequestAmount = (
  number: number | undefined,
  pricing: Pricing | undefined,
  decimalPlaces: number,
  roundMode: 'half' | 'ceil' = 'half'
): string => {
  const value = getMajorAmount(number, pricing);
  const factor = 10 ** decimalPlaces;
  const scaled = value * factor + Number.EPSILON;
  const rounded = roundMode === 'ceil' ? Math.ceil(scaled - Number.EPSILON) / factor : Math.round(scaled) / factor;
  return rounded.toFixed(decimalPlaces);
};

/** Google Pay `totalPrice` allows at most 2 decimal places for every currency. */
export const getNuveiGooglePayPaymentRequestAmount = (number?: number, pricing?: Pricing): string =>
  getRoundedPaymentRequestAmount(number, pricing, DEFAULT_DECIMAL_PLACES);

/**
 * Amount string for Nuvei `createPayment` / openOrder parity.
 * Matches psps-management `convertFromMinorToMajor(...).toString()` — do not force 2dp.
 */
export const getNuveiChargeAmount = (number?: number, pricing?: Pricing): string => {
  const value = getMajorAmount(number, pricing);
  const multiplier = pricing?.multiplier ?? 1;
  return multiplier === 1 ? Math.round(value).toString() : value.toString();
};

export const isNuveiGooglePayPaymentRequestAmountSupported = (_number?: number, _pricing?: Pricing): boolean => true;

/**
 * Apple Pay JS `total.amount` must match `-?[0-9]+(\\.[0-9][0-9])?` — 0 or
 * exactly 2 fraction digits. Never 1 (e.g. `"2177.5"`) and never 3
 * (`"2177.500"` from IQD multiplier).
 *
 * After ZIP/tax, IQD often becomes a half-dinar (`2177500` → UI `2,177.500`).
 * `String(2177.5)` is Apple-invalid; CLDR-0 rounding to `"2178"` also broke
 * sheet open in practice. Always emit max 2 decimals (`"2177.50"`).
 *
 * Use ceil so the sheet never authorizes less than the Nuvei openOrder charge
 * when the major amount has >2 fraction digits (e.g. `2177.501` → `"2177.51"`).
 * See: https://developer.apple.com/documentation/applepayontheweb/applepaylineitem/amount
 */
export const getApplePayPaymentRequestAmount = (number?: number, pricing?: Pricing): string =>
  getRoundedPaymentRequestAmount(number, pricing, DEFAULT_DECIMAL_PLACES, 'ceil');

/** Apple's amount rules are Apple's, not the PSP's — same formatter either way. */
export const getNuveiApplePayPaymentRequestAmount = getApplePayPaymentRequestAmount;

export const isNuveiApplePayPaymentRequestAmountSupported = (_number?: number, _pricing?: Pricing): boolean => true;
